import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { Clock, User, Users, FileText, Award, XCircle, ChevronDown, ChevronUp, Search, Edit, ChevronLeft, ChevronRight, CheckCircle, Trash2 } from "lucide-react";

import { api } from "../../../services/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Label } from "../../../components/ui/label";
import { DateHourPicker } from "../../../components/DateHourPicker";
import { DetalheRelatorio } from "./components/DetalheRelatorio";
import { EditTreinamentoModal } from "./components/EditTreinamentoModal";
import { RelatarErrosModal } from "./components/RelatarErrosModal";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import { MilitarSelectField } from "../../../components/MilitarSelectField";

interface RelatorioTreinamento {
  id: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  treinamento: Treinamento;
  oficial_responsavel: string;
  criado_em: string;
  patente_treinador: string;
  treinador: Militar;
  patente_auxiliar: string;
  auxiliar: Militar;
  sala: string;
  aprovados: Aprovados[];
  reprovados: Reprovados[];
  qtd_treinados: number;
  qtd_aprovados: number;
  observacao: string;
  tipo_relatorio: string;
  status?: string;
  militar_aceitou?: {
    id: string;
    nick: string;
  };
}

interface Treinamento {
  id: string;
  treinamento: string;
}

interface Militar {
  nick: string;
}

interface Aprovados {
  nick: string;
}

interface Reprovados {
  nick: string;
  motivo: string;
  horario: string;
}

interface Militar {
  id: string;
  nick: string;
  patente: {
    patente: string;
  };
}

interface FiltroTreinamentos {
  id_oficial?: string;
  id_treinador?: string;
  id_auxiliar?: string;
  id_treinamento?: string;
  id_treinamento_relatorio?: string;
  nick_treinado?: string;
  inicio_treinamento?: string;
  termino_treinamento?: string;
}

const getStatusIcon = (aprovados: number, reprovados: number) => {
  if (reprovados === 0) return <Award className="h-4 w-4 text-green-600" />;
  if (aprovados > reprovados) return <Users className="h-4 w-4 text-yellow-600" />;
  return <XCircle className="h-4 w-4 text-red-600" />;
};

const getStatusText = (aprovados: number, reprovados: number) => {
  if (reprovados === 0) return "Todos Aprovados";
  if (aprovados > reprovados) return "Maioria Aprovada";
  return "Muitos Reprovados";
};

const getStatusColor = (aprovados: number, reprovados: number) => {
  if (reprovados === 0) return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
  if (aprovados > reprovados) return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700";
  return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700";
};

export function Treinamentos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [relatoriosTreinamento, setRelatoriosTreinamento] = useState<
    RelatorioTreinamento[]
  >([]);
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  const { hasPermission } = usePermissions();
  const modoRestrito = !hasPermission(PERMISSOES.LISTAR_RELATORIOS);
  const canEdit = hasPermission(PERMISSOES.EDITAR_RELATORIO);
  const canDelete = hasPermission(PERMISSOES.EXCLUIR_RELATORIO);

  const [treinamentos, setTreinamentos] = useState<Treinamento[]>([]);
  const [filtros, setFiltros] = useState<FiltroTreinamentos>({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [errosModalOpen, setErrosModalOpen] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState<RelatorioTreinamento | null>(null);

  useEffect(() => {
    carregarDados();
    if (!modoRestrito) {
      carregarTreinamentos();
    }

    // Definir filtro padrão para o dia atual (de 4h até 3:59h do dia seguinte)
    const agora = moment();
    let inicioDia;
    let fimDia;

    if (agora.hour() < 4) {
      // Antes das 4h da manhã - considera desde as 4h de ontem até 3:59h de hoje
      inicioDia = moment().subtract(1, 'day').hour(4).minute(0).second(0);
      fimDia = moment().hour(3).minute(59).second(59);
    } else {
      // Depois das 4h - considera desde as 4h de hoje até 3:59h de amanhã
      inicioDia = moment().hour(4).minute(0).second(0);
      fimDia = moment().add(1, 'day').hour(3).minute(59).second(59);
    }

    setFiltros({
      inicio_treinamento: inicioDia.format('YYYY-MM-DDTHH:mm'),
      termino_treinamento: fimDia.format('YYYY-MM-DDTHH:mm'),
    });
  }, []);

  // Verificar se há um editId na URL para abrir a modal automaticamente
  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId && relatoriosTreinamento.length > 0) {
      const relatorio = relatoriosTreinamento.find(r => r.id === editId);
      if (relatorio) {
        abrirModalEdicao(relatorio);
        // Remove o parâmetro da URL
        searchParams.delete('editId');
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, relatoriosTreinamento]);

  const carregarDados = async (currentPage = page) => {
    setLoading(true);
    try {
      const endpoint = modoRestrito ? "/relatorioTreinamento/meus" : "/relatorioTreinamento";
      const params = modoRestrito
        ? {
            inicio_treinamento: filtros.inicio_treinamento,
            termino_treinamento: filtros.termino_treinamento,
            page: currentPage,
            size: PAGE_SIZE,
          }
        : {
            ...filtros,
            page: currentPage,
            size: PAGE_SIZE,
          };
      const response = await api.get(endpoint, { params });
      setRelatoriosTreinamento(response.data.data);
      setTotalCount(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Erro ao carregar relatórios:", error);
    } finally {
      setLoading(false);
    }
  };

  const carregarTreinamentos = async () => {
    try {
      const response = await api.get("/treinamentos");
      setTreinamentos(response.data);
    } catch (error) {
      console.error("Erro ao carregar treinamentos:", error);
    }
  };

  useEffect(() => {
    setPage(1);
    carregarDados(1);
  }, [filtros]);

  function toggleVisibility(id: string) {
    setIsVisible(prevState => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  }

  const handleFiltroChange = (campo: keyof FiltroTreinamentos, valor: string) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor || undefined
    }));
    setPage(1);
  };

  const limparFiltros = () => {
    setFiltros({});
    setPage(1);
  };

  // Funções de navegação de página
  const handlePrevPage = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      carregarDados(newPage);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      carregarDados(newPage);
    }
  };

  const handleGoToPage = (pageNum: number) => {
    setPage(pageNum);
    carregarDados(pageNum);
  };

  const abrirModalEdicao = (relatorio: RelatorioTreinamento) => {
    setRelatorioSelecionado(relatorio);
    setEditModalOpen(true);
  };

  const abrirModalErros = (relatorio: RelatorioTreinamento) => {
    setRelatorioSelecionado(relatorio);
    setErrosModalOpen(true);
  };

  const handleExcluirRelatorio = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este relatório de treinamento? Esta ação é irreversível.")) {
      return;
    }

    try {
      await api.delete(`/relatorioTreinamento/${id}`);
      alert("Relatório excluído com sucesso!");
      carregarDados(page);
    } catch (error) {
      console.error("Erro ao excluir relatório:", error);
      alert("Erro ao excluir relatório. Tente novamente.");
    }
  };

  const fecharModais = () => {
    setEditModalOpen(false);
    setErrosModalOpen(false);
    setRelatorioSelecionado(null);
  };

  // Funções de seleção
  const handleSelectOficial = (militarId: string, militarNick: string) => {
    handleFiltroChange("id_oficial", militarId);
  };

  const handleSelectTreinador = (militarId: string, militarNick: string) => {
    handleFiltroChange("id_treinador", militarId);
  };

  const handleSelectAuxiliar = (militarId: string, militarNick: string) => {
    handleFiltroChange("id_auxiliar", militarId);
  };



  return (
    <div className="space-y-6">
        <div className="border-b border-border pb-4">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
            Relatórios de Treinamento
          </h1>
          <p className="text-muted-foreground mt-2">
            Visualize e gerencie os relatórios de treinamento realizados
          </p>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Treinamentos
              </CardTitle>
              <CardDescription>
                {modoRestrito
                  ? "Filtros por período para exibir seus próprios relatórios"
                  : "Filtre por ID, oficial, treinador, auxiliar, treinado, tipo de treinamento ou período"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); carregarDados(); }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* ID do Relatório */}
                  {!modoRestrito && (
                  <div>
                    <Label htmlFor="id_relatorio">ID do Relatório</Label>
                    <input
                      id="id_relatorio"
                      type="text"
                      placeholder="Digite o ID do relatório"
                      value={filtros.id_treinamento_relatorio || ""}
                      onChange={(e) => handleFiltroChange("id_treinamento_relatorio", e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  )}

                  {/* Oficial Responsável */}
                  {!modoRestrito && (
                  <div>
                    <Label>Oficial Responsável</Label>
                    <div className="mt-1">
                      <MilitarSelectField
                        placeholder="Buscar oficial..."
                        onChange={handleSelectOficial}
                      />
                    </div>
                  </div>
                  )}

                  {/* Treinador */}
                  {!modoRestrito && (
                  <div>
                    <Label>Treinador</Label>
                    <div className="mt-1">
                      <MilitarSelectField
                        placeholder="Buscar treinador..."
                        onChange={handleSelectTreinador}
                      />
                    </div>
                  </div>
                  )}

                  {/* Auxiliar */}
                  {!modoRestrito && (
                  <div>
                    <Label>Auxiliar</Label>
                    <div className="mt-1">
                      <MilitarSelectField
                        placeholder="Buscar auxiliar..."
                        onChange={handleSelectAuxiliar}
                      />
                    </div>
                  </div>
                  )}

                  {/* Tipo de Treinamento */}
                  {!modoRestrito && (
                  <div>
                    <Label htmlFor="tipo_treinamento">Tipo de Treinamento</Label>
                    <select
                      id="tipo_treinamento"
                      value={filtros.id_treinamento || ""}
                      onChange={(e) => handleFiltroChange("id_treinamento", e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Selecione o tipo de treinamento</option>
                      {treinamentos.map(treinamento => (
                        <option key={treinamento.id} value={treinamento.id}>
                          {treinamento.treinamento}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}

                  {/* Treinado */}
                  {!modoRestrito && (
                  <div>
                    <Label htmlFor="treinado">Treinado</Label>
                    <input
                      id="treinado"
                      type="text"
                      placeholder="Digite o nick do treinado..."
                      value={filtros.nick_treinado || ""}
                      onChange={(e) => handleFiltroChange("nick_treinado", e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  )}

                  {/* Data de Início */}
                  <div>
                    <DateHourPicker
                      title="Data Início"
                      name="data_inicio"
                      selected={filtros.inicio_treinamento ? moment(filtros.inicio_treinamento).toDate() : undefined}
                      onChange={(date) => handleFiltroChange("inicio_treinamento", date ? moment(date).format('YYYY-MM-DDTHH:mm') : "")}
                    />
                  </div>

                  {/* Data de Término */}
                  <div>
                    <DateHourPicker
                      title="Data Fim"
                      name="data_fim"
                      selected={filtros.termino_treinamento ? moment(filtros.termino_treinamento).toDate() : undefined}
                      onChange={(date) => handleFiltroChange("termino_treinamento", date ? moment(date).format('YYYY-MM-DDTHH:mm') : "")}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    disabled={loading}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      limparFiltros();
                      carregarDados(1);
                    }}
                    disabled={loading}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {totalCount} relatório(s) de treinamento encontrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : relatoriosTreinamento.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum relatório de treinamento encontrado
                </div>
              ) : (
                <>
                <div className="space-y-4">
          {relatoriosTreinamento.map(
            ({
              id,
              inicio_treinamento,
              termino_treinamento,
              treinamento,
              oficial_responsavel,
              criado_em,
              patente_treinador,
              treinador,
              patente_auxiliar,
              auxiliar,
              sala,
              aprovados,
              reprovados,
              qtd_treinados,
              qtd_aprovados,
              observacao,
              tipo_relatorio,
              status,
              militar_aceitou,
            }) => (
              <Card key={id} className="hover:shadow-lg transition-shadow border-l-4 border-l-[oklch(0.45_0.15_142)]">
                <CardHeader className="pb-3">
                  <div className="space-y-2">
                    {/* Título + badge tipo */}
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] uppercase">
                        {treinamento.treinamento}
                      </CardTitle>
                      {tipo_relatorio === "treinamento" && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 text-xs">
                          Treinamento
                        </Badge>
                      )}
                      {tipo_relatorio === "teste" && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700 text-xs">
                          Teste
                        </Badge>
                      )}
                    </div>

                    {/* ID + data numa linha, truncando o ID no mobile */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                      <FileText className="h-4 w-4 shrink-0" />
                      <span className="truncate max-w-[90px] sm:max-w-none">ID: {id}</span>
                      <span className="shrink-0">·</span>
                      <Clock className="h-4 w-4 shrink-0" />
                      <span className="shrink-0">
                        {moment(inicio_treinamento).format("DD/MM/YYYY HH:mm")} - {moment(termino_treinamento).format("HH:mm")}
                      </span>
                    </div>

                    {/* Responsável + badges de status na mesma linha */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm min-w-0">
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-foreground truncate">
                          Responsável: <span className="font-medium">{oficial_responsavel.split(' ').pop()}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`${getStatusColor(qtd_aprovados, reprovados.length)} flex items-center gap-1`}>
                          {getStatusIcon(qtd_aprovados, reprovados.length)}
                          {getStatusText(qtd_aprovados, reprovados.length)}
                        </Badge>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {qtd_aprovados}/{qtd_treinados}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>Sala: {sala}</span>
                      <span>Treinador: {patente_treinador} {treinador.nick}</span>
                      {auxiliar && (
                        <span>Auxiliar: {patente_auxiliar} {auxiliar.nick}</span>
                      )}
                      {status === "A" && (
                        <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Aceito{militar_aceitou && ` por ${militar_aceitou.nick}`}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      {canEdit && <Button
                        variant="outline"
                        size="sm"
                        onClick={() => abrirModalEdicao({
                          id,
                          inicio_treinamento,
                          termino_treinamento,
                          treinamento,
                          oficial_responsavel,
                          criado_em,
                          patente_treinador,
                          treinador,
                          patente_auxiliar,
                          auxiliar,
                          sala,
                          aprovados,
                          reprovados,
                          qtd_treinados,
                          qtd_aprovados,
                          observacao,
                          tipo_relatorio,
                        })}
                        className="border-blue-400 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-950"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </Button>}
                      {canEdit && <Button
                        variant="outline"
                        size="sm"
                        onClick={() => abrirModalErros({
                          id,
                          inicio_treinamento,
                          termino_treinamento,
                          treinamento,
                          oficial_responsavel,
                          criado_em,
                          patente_treinador,
                          treinador,
                          patente_auxiliar,
                          auxiliar,
                          sala,
                          aprovados,
                          reprovados,
                          qtd_treinados,
                          qtd_aprovados,
                          observacao,
                          tipo_relatorio,
                        })}
                        className="border-orange-400 text-orange-600 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-400 dark:hover:bg-orange-950"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Relatar Erros
                      </Button>}
                      {canDelete && status !== "I" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExcluirRelatorio(id)}
                          className="border-red-400 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-950"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Excluir
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleVisibility(id)}
                        className="border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.45_0.15_142)] hover:text-white"
                      >
                        {isVisible[id] ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            Ocultar Detalhes
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            Ver Detalhes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {isVisible[id] && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <DetalheRelatorio
                        id={id}
                        inicio_treinamento={inicio_treinamento}
                        termino_treinamento={termino_treinamento}
                        treinamento={treinamento}
                        oficial_responsavel={oficial_responsavel}
                        criado_em={criado_em}
                        patente_treinador={patente_treinador}
                        treinador={treinador}
                        patente_auxiliar={patente_auxiliar}
                        auxiliar={auxiliar}
                        sala={sala}
                        aprovados={aprovados}
                        reprovados={reprovados}
                        qtd_treinados={qtd_treinados}
                        qtd_aprovados={qtd_aprovados}
                        observacao={observacao}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          )}
                </div>
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                    <div className="text-sm text-muted-foreground">
                      Página {page} de {totalPages} ({totalCount} registros)
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={page === 1 || loading}
                        className="flex items-center gap-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleGoToPage(pageNum)}
                              disabled={loading}
                              className={page === pageNum ? "bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)]" : ""}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={page === totalPages || loading}
                        className="flex items-center gap-1"
                      >
                        Próximo
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Relatórios</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {totalCount}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Carregados</span>
                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-700">
                    {relatoriosTreinamento.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Aprovados (Página)</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {relatoriosTreinamento.reduce((acc, rel) => acc + rel.qtd_aprovados, 0)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Reprovados (Página)</span>
                  <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700">
                    {relatoriosTreinamento.reduce((acc, rel) => acc + rel.reprovados.length, 0)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modais */}
      <EditTreinamentoModal
        isOpen={editModalOpen}
        onClose={fecharModais}
        relatorio={relatorioSelecionado}
        onSuccess={carregarDados}
      />

      <RelatarErrosModal
        isOpen={errosModalOpen}
        onClose={fecharModais}
        relatorio={relatorioSelecionado}
        onSuccess={carregarDados}
      />
    </div>
  );
}
